import React, { useRef, useEffect, forwardRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import './index.css';

// Redesigned components
import Header from './components/home/Header';
import HeroSection from './components/home/HeroSection';
import AboutSection from './components/home/AboutSection';
import ServicesSection from './components/home/services/ServicesSection';
import ProjectsSection from './components/home/ProductsSection';
import ContactSection from './components/home/ContactSection';
import Footer from './components/home/Footer';

// ページ全体の動画背景
const GlobalVideoBackground = styled(
  forwardRef((props, ref) => <video ref={ref} className="global-video-background" {...props} />)
)({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  zIndex: 0,
  pointerEvents: 'none',
  // 動画コントロールを非表示にする（全ブラウザ対応）
  '&::-webkit-media-controls': {
    display: 'none !important',
  },
  '&::-webkit-media-controls-enclosure': {
    display: 'none !important',
  },
  '&::-webkit-media-controls-play-button': {
    display: 'none !important',
  },
  '&::-webkit-media-controls-start-playback-button': {
    display: 'none !important',
  },
  '&::-webkit-media-controls-panel': {
    display: 'none !important',
  },
  // モバイルでの再生ボタンを非表示にする
  '@media (max-width: 768px)': {
    '&::-webkit-media-controls': {
      display: 'none !important',
    },
    '&::-webkit-media-controls-enclosure': {
      display: 'none !important',
    },
    '&::-webkit-media-controls-play-button': {
      display: 'none !important',
    },
    '&::-webkit-media-controls-start-playback-button': {
      display: 'none !important',
    },
    '&::-webkit-media-controls-panel': {
      display: 'none !important',
    },
  },
});

function App() {
  const videoRef = useRef(null);
  const hasUserInteracted = useRef(false);
  const isPlaying = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // controls属性を確実に削除
    video.removeAttribute('controls');
    video.controls = false;
    
    // モバイルでの再生ボタンを非表示にするための追加処理
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true');
    video.setAttribute('x5-video-player-type', 'h5');
    video.setAttribute('x5-video-player-fullscreen', 'true');
    
    // iOS Safari特有の処理：動画要素に直接スタイルを適用
    video.style.webkitAppearance = 'none';
    video.style.outline = 'none';
    
    // mutedを確実に設定（モバイルでの自動再生に必要）
    video.muted = true;
    video.volume = 0;
    
    // 動画の再生を試みる関数
    const attemptPlay = async () => {
      // 既に再生中の場合は何もしない
      if (isPlaying.current) return;
      
      try {
        // 動画が読み込まれているか確認
        if (video.readyState >= 2) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            await playPromise;
            // 再生成功
            isPlaying.current = true;
            hideControls();
          }
        }
      } catch (error) {
        // 自動再生が失敗した場合
        console.log('Video autoplay prevented:', error);
        // ユーザー操作が既にある場合は再度試みる
        if (hasUserInteracted.current) {
          setTimeout(() => attemptPlay(), 100);
        }
      }
    };
    
    // ユーザー操作を検知して再生を試みる
    const handleUserInteraction = () => {
      if (!hasUserInteracted.current) {
        hasUserInteracted.current = true;
        if (!isPlaying.current) {
          attemptPlay();
        }
      }
    };
    
    // ユーザー操作イベント（スクロール、タッチ、クリックなど）
    const userInteractionEvents = ['touchstart', 'touchend', 'click', 'scroll', 'wheel', 'mousedown'];
    userInteractionEvents.forEach(eventType => {
      document.addEventListener(eventType, handleUserInteraction, { once: true, passive: true });
    });
    
    // コントロール要素を非表示にするための追加処理
    const hideControls = () => {
      // controls属性が再び追加されないようにする
      if (video.hasAttribute('controls')) {
        video.removeAttribute('controls');
        video.controls = false;
      }
      
      // 動画要素に直接スタイルを適用してコントロールを非表示にする
      const style = document.createElement('style');
      style.id = 'global-video-hide-controls';
      style.textContent = `
        video.global-video-background::-webkit-media-controls {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        video.global-video-background::-webkit-media-controls-enclosure {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        video.global-video-background::-webkit-media-controls-play-button {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          width: 0 !important;
          height: 0 !important;
        }
        video.global-video-background::-webkit-media-controls-start-playback-button {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
          width: 0 !important;
          height: 0 !important;
        }
        video.global-video-background::-webkit-media-controls-panel {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
        video.global-video-background {
          -webkit-appearance: none !important;
          outline: none !important;
        }
      `;
      
      // 既存のスタイルがなければ追加
      if (!document.getElementById('global-video-hide-controls')) {
        document.head.appendChild(style);
      }
    };
    
    // 複数のイベントで再生を試みる
    const events = ['loadeddata', 'loadedmetadata', 'canplay', 'canplaythrough'];
    const playHandlers = events.map(event => {
      const handler = () => {
        if (!isPlaying.current) {
          attemptPlay();
        }
      };
      video.addEventListener(event, handler);
      return { event, handler };
    });
    
    // コントロール非表示のイベントハンドラ
    const controlHideHandlers = ['loadedmetadata', 'canplay', 'playing', 'play'].map(event => {
      const handler = () => {
        if (!video.paused) {
          isPlaying.current = true;
        }
        hideControls();
      };
      video.addEventListener(event, handler);
      return { event, handler };
    });
    
    // 初期実行
    hideControls();
    // 初期自動再生を試みる
    attemptPlay();
    
    // MutationObserverで動的に追加されるcontrols属性を監視
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'controls') {
          if (video.hasAttribute('controls')) {
            video.removeAttribute('controls');
            video.controls = false;
          }
        }
      });
      hideControls();
    });
    
    observer.observe(video, {
      attributes: true,
      attributeFilter: ['controls']
    });
    
    // 定期的にコントロールを非表示にする（iOS Safari対策）
    const hideControlsInterval = setInterval(() => {
      hideControls();
      if (video.hasAttribute('controls')) {
        video.removeAttribute('controls');
        video.controls = false;
      }
    }, 100);
    
    return () => {
      // イベントリスナーの削除
      playHandlers.forEach(({ event, handler }) => {
        video.removeEventListener(event, handler);
      });
      controlHideHandlers.forEach(({ event, handler }) => {
        video.removeEventListener(event, handler);
      });
      
      // ユーザー操作イベントの削除
      userInteractionEvents.forEach(eventType => {
        document.removeEventListener(eventType, handleUserInteraction);
      });
      
      // ObserverとIntervalのクリーンアップ
      observer.disconnect();
      clearInterval(hideControlsInterval);
      
      // スタイル要素の削除
      const styleElement = document.getElementById('global-video-hide-controls');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <Box sx={{ position: 'relative', minHeight: '100vh' }}>
            {/* ページ全体の動画背景 */}
            <GlobalVideoBackground 
              ref={videoRef}
              autoPlay 
              loop 
              muted 
              playsInline
              preload="auto"
              controls={false}
              controlsList="nodownload nofullscreen noremoteplayback"
              disablePictureInPicture
            >
              <source src="/stella_hero.mp4" type="video/mp4" />
            </GlobalVideoBackground>

            {/* Fixed Header */}
            <Header />

            {/* Hero Section */}
            <HeroSection />

            {/* About Section */}
            <AboutSection />

            {/* Services Section */}
            <ServicesSection />

            {/* Projects Section */}
            <ProjectsSection />

            {/* Contact Section */}
            <ContactSection />

            {/* Footer Section */}
            <Footer />
          </Box>
        } />
      </Routes>
    </Router>
  );
}

export default App;
